import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError, newIdempotencyKey } from "@/lib/api";

/**
 * /app/venue — the venue (merchant) console.
 *
 * Flow per Manuel (2026-08-05):
 *   1. No org yet       → registration form (POST /api/v1/partner/register).
 *   2. Org, unpaid      → tier cards with PAYMENT BUTTON (Host Hotel $1,000 /
 *                          Favorite Restaurant $500 → Stripe) + free-tier note.
 *                          Events can be drafted but are NOT visible until paid
 *                          (server enforces via the calendar exposure gate).
 *   3. Org, paid/waived → event form (day, all-day or start/end, the discount
 *                          you give = teaser, description, image URL) + list
 *                          of own events with status.
 */

export const Route = createFileRoute("/app/venue")({
  component: VenueConsole,
});

/* ---------------------------------------------------------------- types */

interface Org {
  id: string; name: string; status: string;
  payment_status: string; partner_tier: string | null;
  types: string[] | string;
}

function organizationTypes(org: Org): string[] {
  return Array.isArray(org.types)
    ? org.types
    : org.types.replace(/^\{|\}$/g, "").split(",").map((type) => type.trim()).filter(Boolean);
}

function isFreePlacement(org: Org): boolean {
  return organizationTypes(org).some((type) => ["artist", "nonprofit"].includes(type));
}

function isParticipationCovered(org: Org): boolean {
  return isFreePlacement(org) || ["paid", "waived"].includes(org.payment_status);
}
interface MyOffer {
  id: string; title: string; category: string; status: string;
  day_date: string | null; starts_time: string | null; ends_time: string | null;
  all_day: boolean;
  publication_type: "free_offer" | "discounted_event";
  teaser: string; description: string; image_url: string | null; capacity: number | null;
}
interface OffersResp { organization: Org; offers: MyOffer[]; }
interface PartnerApplication {
  id: string; role: string; org_name: string; contact_person: string;
  phone: string; email: string; website: string | null; status: string;
  inferred_type: string; linked_organization_id: string | null;
}

type PartnerRole = "restaurant" | "business" | "hotel" | "artist" | "nonprofit";

const CATEGORIES = ["fnb","wellness","music","art","hospitality","tour","experience","workshop"] as const;

/* ---------------------------------------------------------------- page */

function VenueConsole() {
  const [editingOffer, setEditingOffer] = useState<MyOffer | null>(null);
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "unregistered"; application: PartnerApplication | null }
    | { kind: "ready"; data: OffersResp }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  const reload = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const data = await api<OffersResp>("/api/v1/partner/offers");
      setState({ kind: "ready", data });
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        try {
          const linked = await api<{ application: PartnerApplication | null }>("/api/v1/partner/application");
          setState({ kind: "unregistered", application: linked.application });
        } catch {
          setState({ kind: "unregistered", application: null });
        }
      }
      else if (e instanceof ApiError && e.status === 401) setState({ kind: "error", message: "Sign in to manage your venue." });
      else setState({ kind: "error", message: e instanceof ApiError ? (e.problem.detail ?? e.message) : String(e) });
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  return (
    <div className="container max-w-3xl space-y-6 py-6">
      <header>
        <h1 className="font-serif text-3xl text-palm">Your venue</h1>
        <p className="text-muted-foreground">Complete your partner registration, participation, and festival events in one place.</p>
      </header>

      <OnboardingProgress current={state.kind === "unregistered" ? 2 : state.kind === "ready" && !["paid","waived"].includes(state.data.organization.payment_status) ? 3 : 4} />

      {state.kind === "loading" && <p className="text-muted-foreground">Loading…</p>}
      {state.kind === "error" && <p className="text-sm text-destructive">{state.message}</p>}
      {state.kind === "unregistered" && (state.application
        ? <RegisterCard application={state.application} onDone={reload} />
        : <PartnerRegistrationCard onDone={reload} />)}
      {state.kind === "ready" && (
        <>
          <StatusCard org={state.data.organization} onChanged={reload} />
          <EventForm
            key={editingOffer?.id ?? "new-event"}
            org={state.data.organization}
            editing={editingOffer}
            onCancelEdit={() => setEditingOffer(null)}
            onSaved={() => { setEditingOffer(null); void reload(); }}
          />
          <MyEvents
            offers={state.data.offers}
            paid={isParticipationCovered(state.data.organization)}
            onEdit={(offer) => {
              setEditingOffer(offer);
              window.setTimeout(() => document.getElementById("venue-event-form")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
            }}
            onChanged={reload}
          />
        </>
      )}
    </div>
  );
}

function OnboardingProgress({ current }: { current: number }) {
  const steps = ["Account", "Partner profile", "Participation", "Events"];
  return <div className="grid grid-cols-4 gap-2" aria-label={`Partner onboarding step ${current} of 4`}>
    {steps.map((step, index) => {
      const number = index + 1;
      const active = number === current;
      const complete = number < current;
      return <div key={step} className="min-w-0">
        <div className={"mb-2 h-1 rounded-full " + (complete || active ? "bg-palm" : "bg-border")} />
        <p className={"truncate text-[10px] font-semibold uppercase tracking-wide sm:text-xs " + (active ? "text-palm" : complete ? "text-palm/70" : "text-muted-foreground")}>{number}. {step}</p>
      </div>;
    })}
  </div>;
}

const ROLE_OPTIONS: Array<{ id: PartnerRole; title: string; description: string }> = [
  { id: "restaurant", title: "Restaurant", description: "Eligible for Favorite Restaurants placement." },
  { id: "business", title: "Other business", description: "A commercial participant eligible for Favorite Restaurants placement." },
  { id: "hotel", title: "Hotel", description: "The only role eligible for Host Hotels placement." },
  { id: "artist", title: "Artist", description: "Free Other Offers participation, subject to approval." },
  { id: "nonprofit", title: "Nonprofit", description: "Free Other Offers participation, subject to approval." },
];

function ToggleChoice({ checked, onChange, label }: { checked: boolean; onChange: (checked: boolean) => void; label: string }) {
  return <label className={"flex cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm " + (checked ? "border-palm bg-sand text-palm" : "bg-background")}>
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    <span>{label}</span>
  </label>;
}

function Requirement({ required = false }: { required?: boolean }) {
  return <span className={required ? "text-xs font-semibold text-destructive" : "text-xs font-normal text-muted-foreground"}>
    {required ? "Required" : "Optional"}
  </span>;
}

function normalizePartnerPhone(value: string): string {
  const trimmed = value.trim();
  if (/^[0-9]{10}$/.test(trimmed)) return `+52${trimmed}`;
  if (/^00[1-9][0-9]{7,14}$/.test(trimmed)) return `+${trimmed.slice(2)}`;
  return trimmed.replace(/[\s()-]/g, "");
}

function PartnerRegistrationCard({ onDone }: { onDone: () => Promise<void> }) {
  const [role, setRole] = useState<PartnerRole>("restaurant");
  const [orgName, setOrgName] = useState("");
  const [contact, setContact] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [instagram, setInstagram] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [summary, setSummary] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [offerings, setOfferings] = useState<string[]>([]);
  const [experiences, setExperiences] = useState<string[]>([]);
  const [wellness, setWellness] = useState<string[]>([]);
  const [parade, setParade] = useState(false);
  const [fundraiser, setFundraiser] = useState(false);
  const [needsVenue, setNeedsVenue] = useState(false);
  const [consentMotifs, setConsentMotifs] = useState(false);
  const [consentPromotion, setConsentPromotion] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = (value: string, values: string[], setValues: (next: string[]) => void) =>
    setValues(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!consentPromotion) {
      setErr("Accept festival promotion to continue your partner registration.");
      return;
    }
    setBusy(true); setErr(null);
    const phoneNumber = normalizePartnerPhone(phone);
    const whatsappNumber = normalizePartnerPhone(whatsapp || phone);
    const shared = {
      org_name: orgName.trim(), contact_person: contact.trim(), phone: phoneNumber, email: "account@authenticated.local",
      website: website.trim(), socials: { instagram: instagram.trim(), facebook: "", tiktok: "", x: "" },
      consent_promotion: consentPromotion,
    };
    const commonCommercial = {
      offerings, experiences, host_nonprofit_fundraiser: fundraiser,
      parade_participation: parade ? ["entertainers"] : [], whatsapp_number: whatsappNumber,
      consent_motifs: consentMotifs, consent_promotion_frequency: consentPromotion ? "twice_month_then_twice_week" : "none",
    };
    const details = role === "restaurant" ? commonCommercial
      : role === "business" ? { ...commonCommercial, business_type: businessType.trim() }
      : role === "hotel" ? {
          ticket_discount_pct: undefined, wellness_offerings: wellness,
          host_nonprofit_fundraiser: fundraiser, parade_participation: parade ? ["entertainers"] : [],
          whatsapp_number: whatsappNumber, consent_motifs: consentMotifs,
          consent_promotion_frequency: consentPromotion ? "twice_month_then_twice_week" : "none",
        }
      : role === "artist" ? { art_description: summary.trim(), parade_willing: parade, parade_how: parade ? "Festival parade participation" : undefined, needs_venue: needsVenue }
      : { mission: summary.trim(), media_links: [], parade_participation: parade, whatsapp_number: whatsappNumber, desired_event_idea: undefined };
    try {
      await api("/api/v1/partner/onboarding", { method: "POST", json: { role, shared, details } });
      await onDone();
      window.location.hash = "participation";
    } catch (error) {
      setErr(error instanceof ApiError ? (error.problem.detail ?? error.message) : "Partner registration could not be saved.");
    } finally { setBusy(false); }
  }

  return <Card>
    <CardHeader>
      <CardTitle>Partner registration</CardTitle>
      <CardDescription>Choose how you participate. This information creates your partner profile and organization without asking for it again.</CardDescription>
    </CardHeader>
    <CardContent>
      <form onSubmit={submit} className="space-y-6">
        <div className="flex flex-wrap gap-x-5 gap-y-2 rounded-lg border bg-sand/40 px-4 py-3 text-sm">
          <span className="flex items-center gap-2 font-medium text-palm"><span className="h-2.5 w-2.5 rounded-full bg-destructive" /> Required to continue</span>
          <span className="flex items-center gap-2 text-muted-foreground"><span className="h-2.5 w-2.5 rounded-full border border-muted-foreground/60" /> Optional, complete if applicable</span>
        </div>
        <div className="space-y-2">
          <Label className="flex items-center justify-between gap-3">Choose your participation type <Requirement required /></Label>
          <div className="grid gap-2 sm:grid-cols-2">
            {ROLE_OPTIONS.map((option) => <button key={option.id} type="button" onClick={() => setRole(option.id)} className={"rounded-xl border p-4 text-left transition-colors " + (role === option.id ? "border-palm bg-sand" : "hover:border-palm/50")}>
              <span className="block font-serif text-lg text-palm">{option.title}</span>
              <span className="mt-1 block text-xs text-muted-foreground">{option.description}</span>
            </button>)}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label htmlFor="partner-org" className="flex items-center justify-between gap-3">Organization or venue name <Requirement required /></Label><Input id="partner-org" required minLength={2} maxLength={160} value={orgName} onChange={(e) => setOrgName(e.target.value)} /></div>
          <div className="space-y-2"><Label htmlFor="partner-contact" className="flex items-center justify-between gap-3">Contact person <Requirement required /></Label><Input id="partner-contact" required minLength={2} maxLength={160} value={contact} onChange={(e) => setContact(e.target.value)} /></div>
          <div className="space-y-2"><Label htmlFor="partner-phone" className="flex items-center justify-between gap-3">Phone <Requirement required /></Label><Input id="partner-phone" required type="tel" pattern="(?:[0-9]{10}|\+[1-9][0-9]{7,14}|00[1-9][0-9]{7,14})" title="For Mexico, enter the 10-digit number. For other countries, include + and country code." value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="7443833655" /><p className="text-xs text-muted-foreground">Mexico: enter 10 digits; we add +52. Other countries: include +country code.</p></div>
          <div className="space-y-2"><Label htmlFor="partner-whatsapp" className="flex items-center justify-between gap-3">WhatsApp <Requirement /></Label><Input id="partner-whatsapp" type="tel" pattern="(?:[0-9]{10}|\+[1-9][0-9]{7,14}|00[1-9][0-9]{7,14})" title="For Mexico, enter the 10-digit number. For other countries, include + and country code." value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="Leave blank to use phone" /></div>
          <div className="space-y-2"><Label htmlFor="partner-web" className="flex items-center justify-between gap-3">Website <Requirement /></Label><Input id="partner-web" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="Leave blank if you do not have one" /><p className="text-xs text-muted-foreground">If provided, include https://</p></div>
          <div className="space-y-2"><Label htmlFor="partner-instagram" className="flex items-center justify-between gap-3">Instagram <Requirement /></Label><Input id="partner-instagram" value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="Leave blank if you do not have one" /></div>
        </div>

        {role === "business" && <div className="space-y-2"><Label htmlFor="partner-business-type" className="flex items-center justify-between gap-3">Business type <Requirement required /></Label><Input id="partner-business-type" required minLength={2} value={businessType} onChange={(e) => setBusinessType(e.target.value)} placeholder="Gallery, market, wellness studio..." /></div>}

        {(role === "restaurant" || role === "business") && <div className="space-y-3">
          <Label className="flex items-center justify-between gap-3">Offers and experiences <Requirement /></Label>
          <div className="grid gap-2 sm:grid-cols-2">
            {[["free_appetizer","Free appetizer"],["sampler","Sampler"],["dessert","Dessert"],["welcome_drink","Welcome drink"]].map(([id,label]) => <ToggleChoice key={id} label={label} checked={offerings.includes(id)} onChange={() => toggle(id, offerings, setOfferings)} />)}
            {[["music","Music"],["face_painting","Face painting"],["astrology","Astrology"],["art_showcase","Art showcase"]].map(([id,label]) => <ToggleChoice key={id} label={label} checked={experiences.includes(id)} onChange={() => toggle(id, experiences, setExperiences)} />)}
          </div>
        </div>}

        {role === "hotel" && <div className="space-y-3"><Label className="flex items-center justify-between gap-3">Wellness offerings <Requirement /></Label><div className="grid gap-2 sm:grid-cols-2">
          {[["yoga","Yoga"],["temazcal","Temazcal"],["clay_ceremony","Clay ceremony"],["dod_gratitude_ceremony","Gratitude ceremony"]].map(([id,label]) => <ToggleChoice key={id} label={label} checked={wellness.includes(id)} onChange={() => toggle(id, wellness, setWellness)} />)}
        </div></div>}

        {(role === "artist" || role === "nonprofit") && <div className="space-y-2"><Label htmlFor="partner-summary" className="flex items-center justify-between gap-3">{role === "artist" ? "Describe your work" : "Organization mission"} <Requirement required /></Label><textarea id="partner-summary" required minLength={10} maxLength={4000} value={summary} onChange={(e) => setSummary(e.target.value)} className="min-h-32 w-full rounded-md border bg-transparent px-3 py-2 text-sm" /></div>}

        <div className="flex items-center justify-between gap-3"><Label>Additional participation preferences</Label><Requirement /></div>
        <div className="grid gap-2 sm:grid-cols-2">
          {(role === "restaurant" || role === "business" || role === "hotel") && <ToggleChoice label="Host a nonprofit fundraiser" checked={fundraiser} onChange={setFundraiser} />}
          <ToggleChoice label="Participate in the festival parade" checked={parade} onChange={setParade} />
          {role === "artist" && <ToggleChoice label="I need a venue" checked={needsVenue} onChange={setNeedsVenue} />}
          {(role === "restaurant" || role === "business" || role === "hotel") && <ToggleChoice label="Use festival motifs and theme" checked={consentMotifs} onChange={setConsentMotifs} />}
        </div>

        <div
          className={`rounded-xl border-2 p-4 transition-colors ${consentPromotion ? "border-palm bg-palm/5" : "border-accent bg-accent/10"}`}
        >
          <label htmlFor="festival-promotion-consent" className="flex cursor-pointer items-start gap-3">
            <input
              id="festival-promotion-consent"
              type="checkbox"
              required
              checked={consentPromotion}
              onChange={(event) => {
                setConsentPromotion(event.target.checked);
                if (event.target.checked) setErr(null);
              }}
              aria-describedby="festival-promotion-help"
              className="mt-1 h-5 w-5 shrink-0 accent-palm"
            />
            <span>
              <span className="flex flex-wrap items-center gap-2 font-semibold text-palm">
                I agree to festival promotion
                <span className={`rounded-full px-2 py-0.5 text-xs uppercase tracking-wide ${consentPromotion ? "bg-palm text-white" : "bg-accent text-palm"}`}>
                  {consentPromotion ? "Accepted" : "Required"}
                </span>
              </span>
              <span id="festival-promotion-help" className="mt-1 block text-sm text-muted-foreground">
                {consentPromotion
                  ? "Thank you. You can now save your profile and continue to participation."
                  : "Accept this consent to activate the button below and continue your registration."}
              </span>
            </span>
          </label>
        </div>

        <div className="rounded-xl border border-palm/20 bg-sand/50 p-4 text-sm text-palm">
          {role === "hotel" ? "Hotels continue to the USD 1,000 Host Hotel package." : role === "restaurant" || role === "business" ? "Restaurants and businesses continue to the USD 500 Favorite Restaurant package." : "Artists and nonprofits continue to the free Other Offers approval path."}
          <span className="mt-1 block text-xs text-muted-foreground">Every approved participant may post one free offer per festival day and one discounted event per festival week.</span>
        </div>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <Button type="submit" size="lg" className="w-full" disabled={busy || !consentPromotion} aria-describedby="festival-promotion-help">
          {busy ? "Saving partner profile..." : consentPromotion ? "Save and continue to participation" : "Accept promotion to continue"}
        </Button>
      </form>
    </CardContent>
  </Card>;
}

/* -------------------------------------------------------------- register */

function RegisterCard({ application, onDone }: { application: PartnerApplication | null; onDone: () => Promise<void> }) {
  const [name, setName] = useState(application?.org_name ?? "");
  const [type, setType] = useState(application?.inferred_type ?? "restaurant");
  const [zone, setZone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api(application ? "/api/v1/partner/application" : "/api/v1/partner/register", {
        method: "POST",
        json: application
          ? { application_id: application.id, name: name.trim(), zone: zone.trim() || undefined }
          : { name: name.trim(), types: [type], zone: zone.trim() || undefined },
      });
      await onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? (e.problem.detail ?? e.message) : "Registration failed");
    } finally { setBusy(false); }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Register your venue</CardTitle>
        <CardDescription>
          {application
            ? "We found your " + application.role + " application for " + application.org_name + ". Review the imported details and continue without entering them again."
            : "Tell us who you are — approval is quick, and you can draft your events right away."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="vname">Venue name</Label>
            <Input id="vname" required minLength={2} maxLength={120} value={name} onChange={(e) => setName(e.target.value)} placeholder="Casa …" />
          </div>
          {application && (
            <div className="rounded-lg border bg-sand/40 p-3 text-sm text-muted-foreground">
              <p><span className="font-medium text-palm">Contact:</span> {application.contact_person}</p>
              <p><span className="font-medium text-palm">Email:</span> {application.email}</p>
              <p><span className="font-medium text-palm">Phone:</span> {application.phone}</p>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="vtype">Participation classification</Label>
              {application ? (
                <>
                  <Input id="vtype" readOnly value={type.replace("_", " ")} className="capitalize" />
                  <p className="text-xs text-muted-foreground">Imported from your partner registration and locked for package eligibility.</p>
                </>
              ) : (
                <select id="vtype" value={type} onChange={(e) => setType(e.target.value)}
                  className="h-9 w-full rounded-md border bg-transparent px-3 text-sm">
                  {["restaurant","hotel","other_business","bar","beach_club","gallery","wellness","music_venue","market","school","nonprofit"].map((t) =>
                    <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                </select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="vzone">Zone</Label>
              <Input id="vzone" maxLength={80} value={zone} onChange={(e) => setZone(e.target.value)} placeholder="Aldea Zamá, Zona Hotelera…" />
            </div>
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <Button type="submit" disabled={busy}>
            {busy ? "Linking…" : application ? "Continue with this application" : "Register venue"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------- status + pay */

const TIERS = [
  {
    id: "host_hotel" as const, name: "Host Hotel", usd: 1000, mxn: 20000,
    perks: "Exclusive to hotels. Placement in Host Hotels, with one free offer per day and one discounted event per festival week.",
  },
  {
    id: "favorite_restaurant" as const, name: "Favorite Restaurant", usd: 500, mxn: 10000,
    perks: "For restaurants and participating businesses. Placement in Favorite Restaurants, with one free offer per day and one discounted event per festival week.",
  },
];

function StatusCard({ org, onChanged }: { org: Org; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [cur, setCur] = useState<"USD" | "MXN">("USD");
  const [err, setErr] = useState<string | null>(null);
  const covered = isParticipationCovered(org);
  const orgTypes = organizationTypes(org);
  const eligibleTiers = TIERS.filter((t) => {
    if (orgTypes.includes("hotel")) return t.id === "host_hotel";
    if (orgTypes.some((type) => ["restaurant", "other_business", "business"].includes(type))) return t.id === "favorite_restaurant";
    return false;
  });

  async function pay(tier: string) {
    setBusy(tier); setErr(null);
    try {
      const resp = await api<{ url: string }>("/api/v1/partner/checkout", {
        method: "POST",
        json: { tier, currency: cur },
        idempotencyKey: newIdempotencyKey(),
      });
      window.location.assign(resp.url);
    } catch (e) {
      setBusy(null);
      setErr(e instanceof ApiError ? (e.problem.detail ?? e.message) : "Checkout failed");
    }
  }

  // Refresh once if we came back from Stripe (?partner_paid=1 lands on /app,
  // but a manual visit here right after paying should reflect fast).
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("partner_paid")) onChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card className={covered ? "border-palm/40" : "border-destructive/40"}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{org.name}</span>
          <span className="text-sm font-normal">
            {covered
              ? <span className="rounded-full bg-palm px-3 py-1 text-xs text-white">Exposure active{org.partner_tier ? ` · ${org.partner_tier.replace("_", " ")}` : ""}</span>
              : <span className="rounded-full bg-destructive px-3 py-1 text-xs text-white">Not visible yet</span>}
          </span>
        </CardTitle>
        <CardDescription>
          Registration: {org.status} · Payment: {org.payment_status}
        </CardDescription>
      </CardHeader>
      {!covered && (
        <CardContent className="space-y-4">
          <p className="text-sm">
            Your events stay hidden from the public calendar until your participation is covered.
            {eligibleTiers.length
              ? " Your package determines placement; the public section cannot be selected separately."
              : " Your registration is eligible for the free Other Offers placement, subject to approval."}
          </p>
          {eligibleTiers.length > 0 && <div className="rounded-xl border-2 border-accent bg-accent/10 p-4 sm:p-5" role="note" aria-label="Choose payment currency by card issuer">
            <p className="text-xs font-bold uppercase tracking-widest text-accent-foreground">Important: choose by card issuer</p>
            <p className="mt-1 font-serif text-xl leading-tight text-palm sm:text-2xl">Pay in the currency of the bank that issued your card.</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <button type="button" onClick={() => setCur("USD")}
                className={"rounded-lg border-2 p-3 text-left transition-colors " + (cur === "USD" ? "border-palm bg-palm text-white" : "border-palm/30 bg-background text-palm hover:border-palm")}>
                <span className="block text-xs font-bold uppercase tracking-wide">American bank</span>
                <span className="mt-1 block text-lg font-semibold">Pay in dollars · USD</span>
              </button>
              <button type="button" onClick={() => setCur("MXN")}
                className={"rounded-lg border-2 p-3 text-left transition-colors " + (cur === "MXN" ? "border-palm bg-palm text-white" : "border-palm/30 bg-background text-palm hover:border-palm")}>
                <span className="block text-xs font-bold uppercase tracking-wide">Mexican bank</span>
                <span className="mt-1 block text-lg font-semibold">Pay in pesos · MXN</span>
              </button>
            </div>
            <p className="mt-3 text-sm text-palm/80">Use the country of your card issuer, not your current location. A card issued by an American bank should pay in USD; a card issued by a Mexican bank should pay in MXN.</p>
          </div>}
          <div className="grid gap-4 md:grid-cols-2">
            {eligibleTiers.map((t) => (
              <div key={t.id} className="rounded-xl border p-4">
                <p className="font-serif text-lg text-palm">{t.name}</p>
                <p className="my-1 font-serif text-3xl text-palm">
                  ${(cur === "USD" ? t.usd : t.mxn).toLocaleString("en-US")} <span className="text-sm">{cur}</span>
                </p>
                <p className="mb-3 text-xs text-muted-foreground">{t.perks}</p>
                <Button className="w-full" disabled={busy !== null} onClick={() => void pay(t.id)}>
                  {busy === t.id ? "Redirecting…" : "Continue to secure payment"}
                </Button>
              </div>
            ))}
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <p className="text-xs text-muted-foreground">
            {eligibleTiers.length ? "Secure checkout by Stripe · payment plans available — contact the festival team." : "Other Offers participants receive the same posting allowance after approval: one free offer per day and one discounted event per festival week."}
          </p>
        </CardContent>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------ event form */

function EventForm({ org, editing, onSaved, onCancelEdit }: { org: Org; editing: MyOffer | null; onSaved: () => void; onCancelEdit: () => void }) {
  const [publicationType, setPublicationType] = useState<"free_offer" | "discounted_event">(editing?.publication_type ?? "free_offer");
  const [title, setTitle] = useState(editing?.title ?? "");
  const [category, setCategory] = useState<string>(editing?.category ?? "fnb");
  const [day, setDay] = useState(editing?.day_date ?? "2026-10-28");
  const [allDay, setAllDay] = useState(editing?.all_day ?? false);
  const [start, setStart] = useState(editing?.starts_time ?? "19:00");
  const [end, setEnd] = useState(editing?.ends_time ?? "");
  const [teaser, setTeaser] = useState(editing?.teaser ?? "");
  const [desc, setDesc] = useState(editing?.description ?? "");
  const [image, setImage] = useState(editing?.image_url ?? "");
  const [imageMeta, setImageMeta] = useState<{ width: number; height: number } | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null); setMsg(null);
    try {
      const eventData = {
          publication_type: publicationType,
          title: title.trim(),
          category,
          day_date: day,
          all_day: allDay,
          ...(allDay ? {} : { starts_time: start, ...(end ? { ends_time: end } : {}) }),
          teaser: teaser.trim(),
          description: desc.trim(),
      };
      const resp = await api<{ moderation?: string }>("/api/v1/partner/offers", {
        method: editing ? "PATCH" : "POST",
        json: editing
          ? { id: editing.id, action: "update", ...eventData, ends_time: allDay ? null : end || null, image_url: image.trim() || null }
          : { ...eventData, ...(image.trim() ? { image_url: image.trim() } : {}) },
      });
      setMsg(editing ? "Changes submitted. This event is pending admin review before it returns to the calendar." : (resp.moderation ?? "Event submitted."));
      setTitle(""); setTeaser(""); setDesc(""); setImage("");
      setImageMeta(null); setImageFailed(false);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? (e.problem.detail ?? e.message) : "Could not save the event");
    } finally { setBusy(false); }
  }

  return (
    <Card id="venue-event-form" className={editing ? "border-accent" : undefined}>
      <CardHeader>
        <CardTitle>{editing ? `Edit event: ${editing.title}` : "Post an event"}</CardTitle>
        <CardDescription>
          {editing
            ? "Any change sends this event back to admin review. It will not be public again until approved."
            : "Pick a festival day, set the time (or all-day), describe what pass holders get, and add an image."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ept">Publication type</Label>
            <select id="ept" value={publicationType} onChange={(e) => setPublicationType(e.target.value as "free_offer" | "discounted_event")}
              className="h-9 w-full rounded-md border bg-transparent px-3 text-sm">
              <option value="free_offer">Free offer - one per festival day</option>
              <option value="discounted_event">Discounted event - one per festival week</option>
            </select>
            <p className="text-xs text-muted-foreground">These limits apply to every participating venue.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="et">Event title</Label>
            <Input id="et" required minLength={4} maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sunset tasting at …" />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="ec">Category</Label>
              <select id="ec" value={category} onChange={(e) => setCategory(e.target.value)}
                className="h-9 w-full rounded-md border bg-transparent px-3 text-sm">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ed">Festival day</Label>
              <Input id="ed" type="date" min="2026-10-28" max="2026-11-27" value={day} onChange={(e) => setDay(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label className="flex h-9 items-center gap-2 pt-5">
                <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
                All-day
              </Label>
            </div>
          </div>

          {!allDay && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="es">Starts</Label>
                <Input id="es" type="time" required value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ee">Ends (optional)</Label>
                <Input id="ee" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="etz">{publicationType === "free_offer" ? "What is included for free" : "Discount for pass holders"}</Label>
            <Input id="etz" required minLength={4} maxLength={200} value={teaser} onChange={(e) => setTeaser(e.target.value)}
              placeholder={publicationType === "free_offer" ? "Free welcome drink with your pass QR" : "15% off the tasting menu with your pass QR"} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edx">Description</Label>
            <textarea id="edx" required minLength={10} maxLength={2000} value={desc} onChange={(e) => setDesc(e.target.value)}
              rows={4} className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              placeholder="What happens, what's included, anything guests should know…" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ei">Image URL (https, optional)</Label>
            <Input
              id="ei"
              type="url"
              maxLength={500}
              value={image}
              onChange={(e) => {
                setImage(e.target.value);
                setImageMeta(null);
                setImageFailed(false);
              }}
              placeholder="https://…"
            />
            <p className="text-xs text-muted-foreground">
              Landscape (horizontal), 16:9. Recommended: 1600 x 900 px, JPG/WebP/PNG, optimized to 1 MB or less.
              The image appears when guests expand your event on the calendar.
            </p>
            {image.trim() && !image.trim().startsWith("https://") && (
              <p className="text-xs text-destructive">Enter a complete HTTPS URL to preview the image.</p>
            )}
            {image.trim().startsWith("https://") && (
              <div className="overflow-hidden rounded-lg border bg-sand/40">
                {!imageFailed ? (
                  <img
                    src={image.trim()}
                    alt="Event image preview"
                    className="aspect-video w-full object-cover"
                    onLoad={(e) => setImageMeta({
                      width: e.currentTarget.naturalWidth,
                      height: e.currentTarget.naturalHeight,
                    })}
                    onError={() => { setImageFailed(true); setImageMeta(null); }}
                  />
                ) : (
                  <p className="px-3 py-8 text-center text-sm text-destructive">
                    This image could not be loaded. Check that the URL is public and points directly to an image.
                  </p>
                )}
                {imageMeta && (
                  <p className="border-t px-3 py-2 text-xs text-muted-foreground">
                    Source: {imageMeta.width} x {imageMeta.height} px
                    {(imageMeta.width < 1200 || imageMeta.height < 675) && " - resolution may look soft"}
                    {Math.abs(imageMeta.width / imageMeta.height - 16 / 9) > 0.08 && " - calendar preview will crop to 16:9"}
                  </p>
                )}
              </div>
            )}
          </div>

          {msg && <p className="rounded-md bg-sand px-3 py-2 text-sm text-palm">{msg}</p>}
          {err && <p className="text-sm text-destructive">{err}</p>}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={busy}>{busy ? "Saving…" : editing ? "Save changes for review" : "Submit event"}</Button>
            {editing && <Button type="button" variant="outline" disabled={busy} onClick={onCancelEdit}>Cancel editing</Button>}
          </div>
          {org.payment_status === "unpaid" && !isFreePlacement(org) && (
            <p className="text-xs text-destructive">
              Reminder: events won't appear on the public calendar until your participation is paid.
            </p>
          )}
          {isFreePlacement(org) && (
            <p className="text-xs text-muted-foreground">
              Your participation is free. New or edited events appear after admin review and publication.
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------- my events */

function MyEvents({ offers, paid, onEdit, onChanged }: { offers: MyOffer[]; paid: boolean; onEdit: (offer: MyOffer) => void; onChanged: () => Promise<void> }) {
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  if (offers.length === 0) return null;
  const STATUS_COPY: Record<string, string> = {
    draft: "Pending approval",
    published: paid ? "Live on the calendar" : "Approved — hidden until payment",
    cancelled: "Cancelled",
    archived: "Archived",
  };
  async function deleteOffer(offer: MyOffer) {
    if (!window.confirm(`Delete “${offer.title}”? This takes effect immediately and cannot be undone.`)) return;
    setDeleting(offer.id); setDeleteError(null);
    try {
      await api("/api/v1/partner/offers", { method: "DELETE", json: { id: offer.id } });
      await onChanged();
    } catch (error) {
      setDeleteError(error instanceof ApiError ? (error.problem.detail ?? error.message) : "Could not delete the event.");
    } finally { setDeleting(null); }
  }
  return (
    <Card>
      <CardHeader><CardTitle>Your events</CardTitle></CardHeader>
      <CardContent className="divide-y">
        {offers.map((o) => (
          <div key={o.id} className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="font-medium text-palm">{o.title}</p>
              <p className="text-xs text-muted-foreground">
                {o.day_date ?? "—"}{o.all_day ? " · all-day" : o.starts_time ? ` · ${o.starts_time}${o.ends_time ? `–${o.ends_time}` : ""}` : ""} · {o.category} · {o.publication_type === "free_offer" ? "free offer" : "discounted event"}
              </p>
              <p className="mt-0.5 text-xs italic text-muted-foreground">{o.teaser}</p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
              <span className={"rounded-full px-2.5 py-0.5 text-[11px] font-medium " +
                (o.status === "published" && paid ? "bg-palm text-white"
                  : o.status === "cancelled" ? "bg-muted text-muted-foreground"
                  : "bg-amber-100 text-amber-900")}>
                {STATUS_COPY[o.status] ?? o.status}
              </span>
              <Button type="button" size="sm" variant="outline" onClick={() => onEdit(o)}>Edit</Button>
              <Button type="button" size="sm" variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10" disabled={deleting === o.id} onClick={() => void deleteOffer(o)}>
                {deleting === o.id ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </div>
        ))}
        {deleteError && <p className="py-3 text-sm text-destructive">{deleteError}</p>}
      </CardContent>
    </Card>
  );
}
